import { useEffect } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";
import { duration } from "@/i18n/format";

const STORAGE_KEY = "vale.sundayCelebrated";

const buildLine = (summary, t) => {
  const n = summary.tasks_done || 0;
  const time = summary.focus_minutes ? duration(summary.focus_minutes, t) : "";
  if (n && time) return t(n === 1 ? "sunday.bothOne" : "sunday.bothMany", { n, time });
  if (n) return t(n === 1 ? "sunday.tasksOne" : "sunday.tasksMany", { n });
  if (time) return t("sunday.focus", { time });
  return t("sunday.gentle");
};

/**
 * A once-per-Sunday soft toast that celebrates the week the user just had.
 * Renders nothing — it's a side-effect component mounted globally inside
 * <AuthProvider>. Dedupe by `week_end` in localStorage so navigating around
 * the app on Sunday doesn't retrigger the toast.
 */
export function QuietSundayCelebration() {
  const { user, loading } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (loading || !user) return;
    if (new Date().getDay() !== 0) return; // Only fire on Sundays.

    (async () => {
      try {
        const { data } = await http.get("/review/week");
        if (!data?.summary || !data?.end) return;
        const alreadyKey = `${STORAGE_KEY}.${data.end}`;
        if (localStorage.getItem(alreadyKey)) return;
        toast(buildLine(data.summary, t), {
          duration: 8000,
          icon: <Sparkles size={16} className="text-[var(--focus)]" />,
          description: t("sunday.description"),
          action: {
            label: t("sunday.seeReview"),
            onClick: () => { window.location.href = "/review"; },
          },
          className: "vale-sunday-toast",
        });
        localStorage.setItem(alreadyKey, new Date().toISOString());
      } catch {
        // Silent — this is a nicety, never let a network hiccup shout at the user.
      }
    })();
  }, [user, loading, t]);

  return null;
}
